(() => {
  if (customElements.get('cave-predictive-search')) return;
  class CavePredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.suggestions = this.querySelector('[data-cave-suggestions]');
      this.status = this.querySelector('[role="status"]');
      this.cache = new Map();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.request?.abort();
        this.close();
        const query = this.input.value.trim();
        if (query.length >= 2) this.timer = setTimeout(() => this.search(query), 150);
      }, options);
      this.addEventListener('keydown', event => {
        this.selectingSuggestion = false;
        if (event.key === 'Escape') { this.close(); this.input.focus(); }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.suggestions.hidden) return;
        const links = [...this.suggestions.querySelectorAll('a')];
        if (!links.length) return;
        const index = links.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? Math.min(index + 1, links.length - 1) : index - 1;
        event.preventDefault();
        (next < 0 ? this.input : links[next]).focus();
      }, options);
      // A touch can blur the input without focusing its link. Keep the list
      // in place until the browser has dispatched the link's native click.
      document.addEventListener('pointerdown', event => {
        this.selectingSuggestion = this.suggestions.contains(event.target);
        if (!this.contains(event.target)) this.close();
      }, { ...options, capture: true });
      const endPointer = () => { this.selectingSuggestion = false; };
      document.addEventListener('click', endPointer, options);
      document.addEventListener('pointercancel', endPointer, options);
      this.addEventListener('focusout', event => {
        if (!this.selectingSuggestion && !this.contains(event.relatedTarget)) this.close();
      }, options);
    }
    disconnectedCallback() { clearTimeout(this.timer); this.request?.abort(); this.controller?.abort(); }
    close() { this.suggestions.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.status.textContent = ''; }
    async search(query) {
      const request = this.request = new AbortController();
      try {
        let results = this.cache.get(query);
        if (!results) {
          const url = new URL(this.dataset.endpoint + '.json', location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product,collection,page,article');
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('resources[limit_scope]', 'all');
          url.searchParams.set('resources[options][unavailable_products]', 'show');
          const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Suggestions unavailable');
          results = (await response.json()).resources.results;
          if (this.cache.size >= 30) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(query, results);
        }
        if (request !== this.request || this.input.value.trim() !== query || !this.contains(document.activeElement)) return;
        const fragment = document.createDocumentFragment();
        let count = 0;
        const groups = [['products', 'Products'], ['collections', 'Collections'], ['pages', 'Pages'], ['articles', 'Guides']];
        if ((results.collections || []).some(item => item.title.trim().toLocaleLowerCase() === query.trim().toLocaleLowerCase())) {
          groups.unshift(groups.splice(1, 1)[0]);
        }
        for (const [key, label] of groups) {
          const items = results[key] || [];
          if (!items.length) continue;
          const heading = document.createElement('p'); heading.textContent = label; fragment.append(heading);
          const list = document.createElement('ul');
          for (const item of items) {
            const url = new URL(item.url, location.origin);
            if (url.origin !== location.origin || !['https:', 'http:'].includes(url.protocol)) continue;
            const row = document.createElement('li'); const link = document.createElement('a');
            link.href = url.href; link.textContent = item.title; row.append(link); list.append(row); count++;
          }
          fragment.append(list);
        }
        this.suggestions.replaceChildren(fragment);
        this.suggestions.hidden = count === 0;
        this.input.setAttribute('aria-expanded', String(count > 0));
        this.status.textContent = count ? count + ' suggestions. Use arrow keys to browse or Enter to search.' : 'No suggestions. Press Enter to search all results.';
      } catch (error) { if (request === this.request) this.close(); }
    }
  }
  customElements.define('cave-predictive-search', CavePredictiveSearch);
})();
